import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { PartViewer, VIEWER_BOX } from './PartViewer';
import type { ViewerOverlay } from './assemblyScene';
import type { PartProfile } from './partProfile';
import { MeshPlaceholder } from './MeshPlaceholder';
import type { Dims, Extent, FaceWords } from './meshAnnotations';
import { PANEL_TITLE_CLASS } from './MeshPanel';
import { clock } from './waitClock';
import { meshCount } from './MeshViewer';
import { useLanguage } from '../../contexts/LanguageContext';
import { modalBackdropCls, modalCardCls, modalCloseCls, wideOverlayCls } from './overlay';

// One mesh, opened big and turnable, over whatever was underneath.
//
// The thumbnails in the Parts DB are a fixed three-quarter view — comparable, and blind
// to everything the object hides from that angle. This is where the underside gets
// looked at, so it is the full viewer and not a larger picture of one: the wireframe
// modes and the triangle counts are the reason to open a mesh at all rather than
// glance at it.
export function MeshModal({
  src, emptyLabel, emptyIcon, heading, took, stages, checks, caption, dims, estimated, pieceWanted, trueScale, builtMm, label, footer, corner, strip,
  picker, viewerCorner,
  orientJobId, contactFace, sides, explodable, pieceLabel, pieceOrder, overlays, keepView, onMeasure, onProfile, dimensionLines, onClose,
}: {
  // NULL for a part that has no mesh yet. The modal still opens, because everything it
  // holds is a way of MAKING one: the measurements are in the footer and the crops of the
  // real part are across the top. What goes in the viewport is an outline of what is
  // missing, and it is replaced by the mesh the moment the first number lands.
  src: string | null;
  // What to put in that outline. Only read when `src` is null.
  emptyLabel?: string;
  // And what the empty frame draws over its label — a spinner, where the mesh is being rewritten.
  emptyIcon?: ReactNode;
  // WHAT THIS IS, AND HOW LONG IT TOOK TO MAKE, before anything else. The heading is the one
  // over the panel the modal opened from — "3D MODEL (GPT-6 ASTRA - HIGH)" — which the modal
  // covers; the time is the whole generation's, every pass of it, which is the number an
  // effort or a pass count is being weighed against.
  heading?: string;
  took?: number | null;
  // WHERE THE PASS ON SCREEN SPENT ITS SECONDS, for a run that kept the two apart — see
  // generationStages. `took` above is the whole run's; these are one pass's.
  stages?: { call: number; build: number; pass: number; passes: number } | null;
  // AND WHAT WAS CHECKED ON IT, under those: each thing found, or that nothing was. Null says
  // nothing — a run made before builds were checked.
  checks?: string[] | null;
  // Shown ABOVE the viewer, not under it. It says what you are looking at, and a label
  // that follows the thing it labels is read after the fact — the strip position in
  // particular is what you check before pressing an arrow, not after.
  caption?: ReactNode;
  // The part's real size, drawn around the object as the panel-sized viewer draws it.
  // Reading those numbers off a small viewport is most of why this opens.
  dims?: Dims;
  // See PartViewer: a ~ on the wanted line where the size was reasoned, not read.
  estimated?: boolean;
  // What each of the model's own meshes should measure, by name — see PartViewer.
  pieceWanted?: Parameters<typeof PartViewer>[0]['pieceWanted'];
  // The mesh is already in real units (a CAD conversion), so it needs no anchor.
  trueScale?: boolean;
  // What the build measured, for a run that measured itself — see PartViewer.
  builtMm?: { x: number; y: number; z: number } | null;
  // WHAT THE THING ON SCREEN IS CALLED, for the viewer's own legend. The panel-sized
  // viewer behind this one is given the part's name and this one was not, so opening a
  // mesh — the one place it fills the screen and is worth naming — replaced "Backrest and
  // rear legs frame" with the words "3D model", which are true of every mesh in the app.
  label?: string;
  // Controls that act on what is on screen, under it. Here and not beside the small
  // viewer, because every one of them is a decision about geometry — and a decision taken
  // from a thumbnail is one taken without looking. The arrows and their two numbers are
  // legible at this size and are guesswork at the other.
  footer?: ReactNode;
  // The meshes this modal treats as one thing to look at, so the camera, the zoom and the
  // part's pose survive a swap between them — and survive the modal itself, which is a
  // second mounting of the viewer the page already had. See PartViewer's keepView. It is the
  // run, and its passes are the swaps.
  keepView?: string;
  // The modal's own top-left, opposite the close. For the one control that acts on the
  // RUN rather than on its geometry — deleting sat in a row of buttons pressed to make
  // the mesh better, and was the only one there that made nothing better.
  corner?: ReactNode;
  // A row across the top of the viewer — the part as it appears in the world, beside the
  // model of it. Above rather than below because it is what the mesh is being COMPARED to,
  // and a comparison reads badly when one of the two is under a fold.
  strip?: ReactNode;
  // WHICH MESH OF SEVERAL, chosen between the pictures and the viewer.
  //
  // It is not a caption and not a control on the mesh, which is why it is neither in the one
  // nor in the footer: it decides what the viewport is SHOWING. So it sits in the last inch
  // before the viewport, with the viewer's own how-is-it-lit switch directly under it — two
  // rows that both change the picture and change nothing in the file.
  picker?: ReactNode;
  // Passed through to the viewer's own top-left, INSIDE the white. Different from `corner`
  // above, which is the modal's: this one sits with the object it is about.
  viewerCorner?: ReactNode;
  // WHICH RUN THE MESH ON SCREEN IS, when the footer under it can ask a model which way round
  // it goes. Passed straight through to the viewer, which writes that answer on the six faces
  // of the box — see PartViewer, where the reason it is an id and not the answer is written.
  // Absent from the catalog's viewer, which draws the same box and asks nobody anything.
  orientJobId?: number;
  contactFace?: keyof FaceWords;
  // WHETHER THE PART COMES IN TWO HANDS — passed straight through, like orientJobId above.
  // The expanded view is where a mesh is judged, and which hand you are judging is part of
  // that: a pair looked at whole is the one place the reflection is legible at all.
  sides?: boolean;
  // Whether this mesh is an assembly whose parts can be spread apart — see PartViewer.
  explodable?: boolean | 'axis';
  // HOW A MESH'S ROW IS NAMED in the parts card, where that differs from its name — see ScenePartCards.
  pieceLabel?: (name: string) => string;
  // The order its rows are listed in — see ScenePartCards.
  pieceOrder?: string[];
  // Solids laid over the model — see PartViewer.
  overlays?: readonly ViewerOverlay[];
  // The mesh's extents in the file's own units, handed up as the panel viewer does: the
  // footer's controls are computed from it, and while this is open there is no other
  // viewer running to report it.
  onMeasure?: (size: Extent) => void;
  // Straight through to the viewer — see PartViewer's own, and partProfile. The modal is one
  // of the two mountings of that viewer and the controls under it are the caller's.
  onProfile?: (profile: PartProfile) => void;
  // Straight through to the viewer — where the dimensions in the footer are measured.
  dimensionLines?: Parameters<typeof PartViewer>[0]['dimensionLines'];
  onClose: () => void;
}) {
  // Escape closes, as it does in the image lightbox beside it. Bound on the document
  // rather than on a focused element: the viewer takes the pointer, and a modal you
  // cannot dismiss without aiming at its corner is worse than no shortcut.
  const { t } = useLanguage();
  // HOW BIG THE MESH ON SCREEN IS, as its viewer counted it — under the time, with the other
  // facts about the model, rather than under the picture.
  const [stats, setStats] = useState<{ vertices: number; triangles: number } | null>(null);
  const counted = src !== null ? stats : null;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className={`${wideOverlayCls} ${modalBackdropCls}`}
    >
      {/* The panel swallows the click that would close the backdrop — turning the object
          means dragging across it, and a drag that ends outside would otherwise dismiss
          the thing being dragged. */}
      <div
        onClick={(e) => e.stopPropagation()}
        // AND MORE AT THE SIDES THAN ANYWHERE, which is the one gutter that costs nothing. The
        // viewer frames its object VERTICALLY (see meshScene), so width beyond what the mesh
        // needs is white either side of it — and a 16px gutter put that white hard against the
        // panel edge, which reads as the mesh being cropped rather than framed. The corner
        // badge and the close button are positioned against the panel, not the padding, so
        // they stay where the eye already looks for them.
        //
        // More room at the bottom than at the top, because that is where the controls are.
        // They were sharing a 16px gutter with the viewer above them and running into each
        // other sideways at the same time; the space has to come from somewhere, and the
        // mesh gives it up more cheaply than the buttons do.
        // TALLER THAN THE SCREEN IS NOW POSSIBLE, so the panel scrolls. It was a fixed
        // block centred in the viewport: fine while the footer was three rows of buttons,
        // and wrong the moment a proposal arrived with forty lines of Blender under it —
        // the overflow went off both ends at once and neither could be reached, since a
        // flex item centred in a fixed overlay has nowhere to scroll to.
        //
        // The viewer inside keeps its own height (54vh), so what scrolls is the controls
        // under it, which is what grew. Wheel over the mesh still zooms the mesh: the
        // canvas takes that event before this ever sees it.
        //
        // AND IT IS A COLUMN, which is what stops it needing to scroll at all on most
        // screens. Everything but the mesh is text and buttons at a fixed height; the mesh
        // is the one thing that can be smaller without anything being lost, so it is the
        // one thing allowed to shrink. On a tall screen it gets its 40vh, on a laptop it
        // gives back whatever the strip and the controls need, and the overflow below is
        // what is left when even that is not enough (a proposal with forty lines of
        // Blender under it, which is where the scrolling came from in the first place).
        className={`relative flex max-h-[92dvh] w-full max-w-5xl flex-col overflow-y-auto px-12 pb-5 pt-4 ${modalCardCls}`}
      >
        {corner && <div className="absolute left-3 top-3 z-10">{corner}</div>}

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={modalCloseCls}
        >
          <X className="h-5 w-5" />
        </button>

        {(heading || took != null || counted || checks) && (
          <div className="mb-2 flex shrink-0 flex-col items-center gap-0.5">
            {heading && <h4 className={PANEL_TITLE_CLASS}>{heading}</h4>}
            {took != null && (
              <p className="text-xs text-slate-500">
                {t('notice.cat.tookTime').replace('{t}', clock(took))}
              </p>
            )}
            {/* THE SAME TWO NAMES THE BUTTON USED while it ran, so the wait somebody sat
                through and the record of it read as the same thing. A call of 0 is a
                rebuild, which had none. Named by its pass where there are several, the line
                above it being the run's total and these one pass's share of it — with that
                pass's own total, which a single pass leaves to the line above. */}
            {stages && (
              <p className="text-[11px] text-slate-400">
                {stages.passes > 1 && `${t('notice.3d.progressPass')
                  .replace('{n}', String(stages.pass)).replace('{of}', String(stages.passes))} — ${
                  t('notice.3d.passTotal').replace('{t}', clock(stages.call + stages.build))}: `}
                {[...(stages.call ? [[t('notice.3d.stage.thinking'), stages.call] as const] : []),
                  [t('notice.3d.stage.building'), stages.build] as const]
                  .map(([name, seconds]) => `${name} ${clock(seconds)}`)
                  .join(' · ')}
              </p>
            )}
            {counted && (
              <p className="text-xs text-slate-500">
                {meshCount(counted.vertices)} vertices · {meshCount(counted.triangles)} triangles
              </p>
            )}
            {checks && (checks.length ? (
              <ul className="mt-0.5 flex max-w-3xl flex-col items-center gap-0.5 text-center text-xs text-amber-600">
                {checks.map((line) => <li key={line}>⚠ {line}</li>)}
              </ul>
            ) : (
              <p className="text-xs text-emerald-600">{t('notice.3d.checksPassed')}</p>
            ))}
          </div>
        )}

        {caption && (
          <div className="mb-2 flex shrink-0 flex-col items-center gap-0.5 text-xs text-slate-500">
            {caption}
          </div>
        )}


        {strip && <div className="mb-2 w-full shrink-0">{strip}</div>}

        {picker && <div className="mb-2 flex shrink-0 justify-center">{picker}</div>}

        {src === null ? (
          <MeshPlaceholder label={emptyLabel ?? ''} icon={emptyIcon} className={VIEWER_BOX} />
        ) : (
        <PartViewer
          src={src}
          dims={dims}
          estimated={estimated}
          pieceWanted={pieceWanted}
          trueScale={trueScale}
          builtMm={builtMm}
          label={label}
          onMeasure={onMeasure}
          onProfile={onProfile}
          onStats={setStats}
          corner={viewerCorner}
          orientJobId={orientJobId}
          contactFace={contactFace}
          sides={sides}
          explodable={explodable}
          pieceLabel={pieceLabel}
          pieceOrder={pieceOrder}
          overlays={overlays}
          keepView={keepView}
          // THE EXPANDED VIEW IS WHERE A MESH IS JUDGED, so it keeps the two questions the
          // phase's own panel leaves out: how the mesh is BUILT (draw modes and the counts)
          // and what colour it really is (even against studio). Same viewer as the panel
          // otherwise — same gizmo, same box, same readout — because it is the same part.
          topology
          lights
          // AND ITS CONTROLS ABOVE THE PICTURE, not over it: here the mesh fills the screen to be
          // judged, and the corners are where it is.
          controlsAbove
          dimensionLines={dimensionLines}
          // Shorter again. Most of a tall box is white: a turned part is a flat disc framed
          // by its bounding SPHERE, so the height above and below it was never going to hold
          // anything, and it was pushing the measurements the mesh has to be compared with
          // below the fold. The framing is vertical (see meshScene), so the object comes out
          // a little smaller too — that is the trade, and a button you can see whole beside
          // its numbers beats a bigger one you have to scroll away from them to read.
          // 40vh is what it ASKS for, not what it always gets: it is the only flexible
          // thing in the column, so on a screen too short for the strip, the controls and
          // the mesh together it gives up height down to this floor before the panel
          // starts to scroll. 208px still shows a whole chair.
          className={VIEWER_BOX}
        />
        )}

        {/* The footer spans the modal rather than hugging its contents: it groups its own
            controls and spreads them across the width, which it can only do if it HAS the
            width. It used to be centred around whatever it contained, which bunched three
            unrelated groups into the middle and left both corners empty. */}
        {footer && <div className="mt-3 w-full shrink-0">{footer}</div>}
      </div>
    </div>
  );
}
